// ═══════════════════════════════════════════════════════════════
// DonorNotesPanel.jsx
// Path: src/components/donorDetail/DonorNotesPanel.jsx
//
// Sidebar panel showing pinned intelligence from contact reports.
// Reads member.pinnedIntelligence — populated when a director or admin
// checks "Pin to donor record" in the contact report form.
//
// Renders nothing when:
//   - The user lacks the contactReports permission
//   - There are zero pinned items
//
// Each pinned card displays:
//   - The section label (FAMILY, OCCUPATION, etc.)
//   - The pinned text
//   - A footer: "Pinned [date] by [name]"
//   - An unpin button (× icon) with confirm dialog
//
// Phase 4 of CRM_Donor_Contact_Reports_Blueprint_v1_0.md
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { Pin, X, ExternalLink } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { contactReportsService } from "../../services/contactReportsService";
import { PINNABLE_KEYS } from "../../constants/contactReportSections";
import { ConfirmDialog } from "../ui/Modal";

const formatPinDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export const DonorNotesPanel = ({ member, palette }) => {
  const {
    canFileContactReports,
    refreshMember,
    showMessage,
    setPrintContactReportId,
  } = useApp();
  const [confirmUnpinKey, setConfirmUnpinKey] = useState(null);
  const [unpinning, setUnpinning] = useState(false);

  // Permission gate
  if (!canFileContactReports) return null;

  const pinned = member?.pinnedIntelligence || {};

  // Build the visible list in PINNABLE_KEYS order so display is stable
  const visiblePins = PINNABLE_KEYS
    .map((p) => ({
      key: p.key,
      label: p.label,
      data: pinned[p.key],
    }))
    .filter((p) => p.data && p.data.text && p.data.text.trim().length > 0);

  if (visiblePins.length === 0) return null;

  const handleUnpin = async (sectionKey) => {
    if (!member?.id) return;
    setUnpinning(true);
    try {
      await contactReportsService.unpinSection(member.id, sectionKey);
      // Refresh the donor record so pinnedIntelligence updates locally
      refreshMember(member.id);
      setConfirmUnpinKey(null);
      showMessage("Pinned note removed", "warning");
    } catch (err) {
      showMessage(
        "Could not unpin — " + (err.message || "unknown error"),
        "error"
      );
    } finally {
      setUnpinning(false);
    }
  };

  return (
    <div
      style={{
        padding: "12px 18px",
        borderBottom: `1px solid ${palette.cardBorder}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 10,
        }}
      >
        <Pin size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
        <span
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 700,
            textTransform: "uppercase",
            color: palette.text3,
            letterSpacing: "0.06em",
            fontFamily: FONT_FAMILY,
          }}
        >
          Donor Notes
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {visiblePins.map((pin) => (
          <div
            key={pin.key}
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              background: `${palette.accent1}08`,
              border: `1px solid ${palette.accent1}30`,
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: palette.accent1,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {pin.label}
              </span>
              <button
                type="button"
                onClick={() => setConfirmUnpinKey(pin.key)}
                title="Unpin from donor record"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  border: "none",
                  background: "transparent",
                  color: palette.text3,
                  cursor: "pointer",
                  flexShrink: 0,
                  padding: 0,
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#fef2f2";
                  e.currentTarget.style.color = "#dc2626";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = palette.text3;
                }}
              >
                <X size={ICON_SIZES.xs} />
              </button>
            </div>
            <div
              style={{
                fontSize: FONT_SIZES.sm,
                color: palette.text1,
                fontFamily: FONT_FAMILY,
                lineHeight: 1.4,
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
              }}
            >
              {pin.data.text}
            </div>
            <div
              style={{
                marginTop: 6,
                paddingTop: 6,
                borderTop: `1px dashed ${palette.accent1}40`,
                fontSize: 10,
                color: palette.text3,
                fontFamily: FONT_FAMILY,
                fontStyle: "italic",
              }}
            >
              Pinned {formatPinDate(pin.data.pinned_at)}
              {pin.data.pinned_by_name ? ` by ${pin.data.pinned_by_name}` : ""}
            </div>
            {pin.data.source_report_id && (
              <button
                type="button"
                onClick={() => setPrintContactReportId(pin.data.source_report_id)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  marginTop: 6,
                  padding: 0,
                  background: "none",
                  border: "none",
                  color: palette.accent1,
                  fontSize: 10,
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                View source report
                <ExternalLink size={ICON_SIZES.xs} />
              </button>
            )}
          </div>
        ))}
      </div>

      {confirmUnpinKey && (
        <ConfirmDialog
          title="Unpin from donor record?"
          message="This removes the pinned note from the donor's permanent record. The original contact report is not affected — you can re-pin from there if needed."
          confirmLabel={unpinning ? "Removing..." : "Unpin"}
          variant="danger"
          onConfirm={() => handleUnpin(confirmUnpinKey)}
          onCancel={() => setConfirmUnpinKey(null)}
        />
      )}
    </div>
  );
};
